import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import { Button, ResetIcon } from '@blade/ui-kit'
import { FilterButton, FilterPopover } from '@root/shared/ui-kit'

import { FilterByActivityOption } from './filter-by-activity-option'

import type {
  CustomFilter,
  TableFilter,
} from '@root/shared/ui-kit/data-table/atoms/table-filter'

export interface FilterByActivityProps<T extends string> {
  filter: TableFilter<string> | undefined
  setColumnFilter: (filter: TableFilter<T>) => void
  clearColumnFilter: () => void
  columnId: T
}

type FilterType = 'active' | 'blocked' | undefined

export const fromUnknown = (x: unknown) => {
  if (x === 'active') {
    return 'active'
  }
  if (x === 'blocked') {
    return 'blocked'
  }
  return undefined
}

export const FilterByActivity = <T extends string>({
  filter,
  setColumnFilter,
  clearColumnFilter,
  columnId,
}: FilterByActivityProps<T>) => {
  if (filter && filter.type !== 'custom') {
    throw new Error('FilterByName: filter must be a custom filter')
  }

  const { t } = useTranslation()
  const [isOpen, setIsOpen] = useState(false)
  const [value, setValue] = useState(filter?.value as FilterType)

  useEffect(() => {
    setValue(filter?.value as FilterType)
  }, [filter])

  const handleApply = () => {
    if (!value) {
      clearColumnFilter()
      setIsOpen(false)
      return
    }
    const filter: CustomFilter<T, FilterType> = {
      type: 'custom',
      column: columnId,
      value,
      predicate: (x) => x === value,
    }
    setColumnFilter(filter as TableFilter<T>)
    setIsOpen(false)
  }

  const handleReset = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.stopPropagation()
    clearColumnFilter()
    setValue(undefined)
    setIsOpen(false)
  }
  const hasFilter = filter !== undefined

  const trigger = (
    <FilterByMakerTrigger>
      <FilterButton active={isOpen || hasFilter} />
      {t('common.activity')}
    </FilterByMakerTrigger>
  )

  return (
    <FilterPopover
      trigger={trigger}
      title={t('common.activity')}
      isOpen={isOpen}
      onOpenChange={setIsOpen}
    >
      <FilterByMakerContent
        onSubmit={(e) => {
          e.preventDefault()
          e.stopPropagation()
          handleApply()
        }}
      >
        <FilterByActivityOption
          value="active"
          active={value === 'active'}
          onClick={() => setValue('active')}
        />
        <FilterByActivityOption
          value="blocked"
          active={value === 'blocked'}
          onClick={() => setValue('blocked')}
        />

        <FilterByMakerFooter>
          <FilterResetButton
            variant="ghost"
            radius="rounded"
            size="xs"
            onClick={handleReset}
            disabled={!value}
          >
            <ResetIcon />
            {t('trade.filters.common.reset')}
          </FilterResetButton>
          <Button
            variant="solid"
            color="primary"
            radius="rounded"
            size="xs"
            onClick={handleApply}
            disabled={!value}
          >
            {t('trade.filters.common.apply')}
          </Button>
        </FilterByMakerFooter>
      </FilterByMakerContent>
    </FilterPopover>
  )
}

const FilterByMakerTrigger = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;

  color: var(--text-11);

  transition: color 0.2s ease-in-out;

  &:hover {
    color: var(--text-12);
  }
`

const FilterByMakerFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const FilterByMakerContent = styled.form`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding-top: 6px;
`

const FilterResetButton = styled(Button)`
  gap: 4px;
  padding-left: 0px;
  font-weight: 400;
  &:hover {
    background: transparent;
  }
`
